/**
 * ScheduleManagement.js - Admin Weekly Schedule Management (Redesigned)
 * 
 * Features:
 * - Clean card-based grid layout
 * - "+ Assign" buttons with hover effects
 * - Week navigation with Previous/Next/Current Week
 * - Available teachers footer showing who can be assigned each day
 * - Dark/Light theme support
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import dataStore from '../data/dataStore';
import RoomCardPrint from '../components/RoomCardPrint';

const OVERTIME_HOURS_THRESHOLD = 40;
const LUNCH_BREAK_MINUTES = 30;

function ScheduleManagement() {
  const { theme, colors, darkMode } = useTheme();
  const { user: currentUser } = useAuth();
  
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [currentMonday, setCurrentMonday] = useState(getMonday(new Date()));
  const [selectedCell, setSelectedCell] = useState(null);
  const [assignmentTimes, setAssignmentTimes] = useState({ startTime: '08:00', endTime: '17:00' });
  const [modalUserTimes, setModalUserTimes] = useState({}); // Per-user times in modal: { userId: { start, end } }
  const [message, setMessage] = useState('');
  
  // Notes state
  const [dailyNotes, setDailyNotes] = useState({}); // { dateStr: { note, type, ... } }
  const [editingDailyNote, setEditingDailyNote] = useState(null); // dateStr being edited
  const [dailyNoteText, setDailyNoteText] = useState('');
  const [dailyNoteType, setDailyNoteType] = useState('theme');
  
  // Room notes state
  const [roomNotes, setRoomNotes] = useState({}); // { 'dateStr_roomId': [notes] }
  const [editingRoomNote, setEditingRoomNote] = useState(null); // { dateStr, roomId }
  const [newNoteText, setNewNoteText] = useState('');
  
  // Week theme state
  const [weekTheme, setWeekTheme] = useState('');
  const [editingTheme, setEditingTheme] = useState(false);
  
  // Print modal state
  const [showPrintModal, setShowPrintModal] = useState(false);
  
  // Create template modal state
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  
  // Apply template modal state
  const [showApplyTemplateModal, setShowApplyTemplateModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [applyOptions, setApplyOptions] = useState({
    skipPTO: true,
    overwriteExisting: false,
    respectAvailability: true
  });
  
  // Edit assignment modal state
  const [editingAssignment, setEditingAssignment] = useState(null); // { assignmentId, userId, roomId, dateStr, startTime, endTime }
  
  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);
  
  // Announcements state
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementForm, setAnnouncementForm] = useState({ message: '', icon: 'ℹ️', date: '' });
  const [announcementsExpanded, setAnnouncementsExpanded] = useState(true);

  // Publish status state
  const [isWeekPublished, setIsWeekPublished] = useState(false);

  // Inject print styles on mount
  useEffect(() => {
    const printStyles = document.createElement('style');
    printStyles.id = 'schedule-print-styles';
    printStyles.innerHTML = `
      @media print {
        @page {
          size: landscape;
          margin: 0.5in 0.4in;
        }
        
        /* Hide browser headers/footers - some browsers respect this */
        html {
          margin: 0;
          padding: 0;
        }
        
        /* Hide everything except the schedule */
        body * {
          visibility: hidden;
        }
        
        /* Show the printable area */
        .print-container,
        .print-container * {
          visibility: visible !important;
        }
        
        .print-container {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          padding: 0 !important;
          max-width: none !important;
        }
        
        /* Hide non-printable elements */
        .no-print {
          display: none !important;
        }
        
        /* Print header */
        .print-header {
          display: block !important;
          text-align: center;
          margin-bottom: 15px;
          padding-bottom: 10px;
          border-bottom: 2px solid #333;
        }
        
        .print-header h1 {
          font-size: 22px;
          margin: 0 0 5px 0;
          color: #000;
        }
        
        .print-header p {
          font-size: 14px;
          margin: 0;
          color: #333;
        }
        
        /* Print announcements */
        .print-announcements {
          display: block !important;
          margin-bottom: 15px;
          padding: 10px;
          border: 1px solid #999;
          border-radius: 4px;
          background: #f9f9f9 !important;
        }
        
        .print-announcements-title {
          font-size: 12px;
          font-weight: bold;
          margin: 0 0 8px 0;
          color: #000;
        }
        
        .print-announcement-item {
          font-size: 11px;
          color: #333;
          margin: 4px 0;
          padding-left: 20px;
        }
        
        /* Grid container */
        .print-grid-container {
          background: transparent !important;
          box-shadow: none !important;
          border-radius: 0 !important;
          overflow: visible !important;
        }
        
        /* Schedule grid container for print */
        .print-schedule-grid {
          display: grid !important;
          grid-template-columns: 60px repeat(5, 1fr) !important;
          gap: 0 !important;
          width: 100% !important;
          font-size: 10px !important;
        }
        
        /* All cells get consistent borders */
        .print-header-cell,
        .print-room-cell,
        .print-day-cell {
          border: 1px solid #888 !important;
          box-sizing: border-box !important;
        }
        
        /* Header cells */
        .print-header-cell {
          background: #d0d0d0 !important;
          color: #000 !important;
          font-weight: bold !important;
          text-align: center !important;
          padding: 8px 6px !important;
        }
        
        .print-header-cell div {
          color: #000 !important;
        }
        
        /* Room label cells */
        .print-room-cell {
          background: #e8e8e8 !important;
          color: #000 !important;
          padding: 8px 6px !important;
          font-weight: bold !important;
          text-align: center !important;
        }
        
        .print-room-cell > div {
          background: transparent !important;
          padding: 2px !important;
        }
        
        .print-room-cell span {
          color: #000 !important;
          font-size: 10px !important;
        }
        
        /* Day cells */
        .print-day-cell {
          background: #fff !important;
          color: #000 !important;
          padding: 6px !important;
          min-height: 50px !important;
        }
        
        /* Keep room rows together - prevent page breaks inside */
        .print-room-row {
          display: grid !important;
          grid-template-columns: 60px repeat(5, 1fr) !important;
          gap: 0 !important;
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }
        
        /* Room row wrapper to keep entire row together */
        .room-row-wrapper {
          display: grid !important;
          grid-template-columns: 60px repeat(5, 1fr) !important;
          gap: 0 !important;
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }

        /* PTO and On Call rows need break-inside avoid too */
        .print-pto-row {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }
        
        /* Assignment tags */
        .print-assignment {
          background: #d4edda !important;
          border: 1px solid #28a745 !important;
          border-radius: 3px !important;
          padding: 4px 6px !important;
          margin: 2px 0 !important;
          color: #000 !important;
        }
        
        .print-assignment-name {
          font-weight: bold !important;
          font-size: 9px !important;
          color: #000 !important;
        }
        
        .print-assignment-time {
          font-size: 8px !important;
          color: #333 !important;
        }
        
        /* PTO row - must match main grid exactly */
        .print-pto-row {
          display: grid !important;
          grid-template-columns: 60px repeat(5, 1fr) !important;
          gap: 0 !important;
        }
        
        .print-pto-row .print-room-cell {
          background: #ffe0e0 !important;
          border: 1px solid #888 !important;
        }
        
        .print-pto-row .print-day-cell {
          background: #fff !important;
          min-height: 30px !important;
          border: 1px solid #888 !important;
        }
        
        .print-pto-badge {
          background: #f8d7da !important;
          border: 1px solid #dc3545 !important;
          border-radius: 3px !important;
          padding: 3px 6px !important;
          font-size: 9px !important;
          color: #000 !important;
        }

        /* Footer */
        .print-footer {
          display: block !important;
          margin-top: 15px;
          padding-top: 8px;
          border-top: 1px solid #999;
          font-size: 10px;
          color: #666;
          text-align: right;
        }
      }
      
      /* Hide print elements on screen */
      @media screen {
        .print-header,
        .print-footer,
        .print-announcements {
          display: none;
        }
      }
    `;
    document.head.appendChild(printStyles);
    
    return () => {
      const existingStyles = document.getElementById('schedule-print-styles');
      if (existingStyles) {
        existingStyles.remove();
      }
    };
  }, []);

  // Get Monday of the current week
  function getMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  // Get array of weekday dates for the current week
  const getWeekDates = useCallback(() => {
    const dates = [];
    for (let i = 0; i < 5; i++) {
      const date = new Date(currentMonday);
      date.setDate(currentMonday.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [currentMonday]);

  // Helper to convert Date to local YYYY-MM-DD string
  const dateToLocalString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load all data
  const loadData = useCallback(() => {
    // Filter rooms to only show those with showOnTeacherSchedule enabled
    setRooms(dataStore.getRooms().filter(r => r.showOnTeacherSchedule !== false));
    // Include teachers and admins on schedule, but not super admins
    setUsers(dataStore.getUsers().filter(u => u.role === 'USER' || u.role === 'ADMIN'));
    const mondayStr = dateToLocalString(currentMonday);
    setSchedule(dataStore.getWeekSchedule(mondayStr));
    
    // Load week theme (stored as daily note with type 'week_theme' on Monday)
    const themeNote = dataStore.getDailyNote(mondayStr);
    if (themeNote && themeNote.type === 'week_theme') {
      setWeekTheme(themeNote.note);
    } else {
      setWeekTheme('');
    }
    setEditingTheme(false);
    
    // Load publish status for this week
    setIsWeekPublished(dataStore.isWeekPublished(mondayStr));
    
    // Load room notes for the week
    const allRooms = dataStore.getRooms();
    const weekDates = getWeekDates();
    const notesMap = {};
    weekDates.forEach(date => {
      const dateStr = dateToString(date);
      allRooms.forEach(room => {
        const notes = dataStore.getRoomDayNotes(room.id, dateStr);
        if (notes && notes.length > 0) {
          notesMap[`${dateStr}_${room.id}`] = notes;
        }
      });
    });
    setRoomNotes(notesMap);
    
    // Load announcements for the week
    setAnnouncements(dataStore.getAnnouncementsForWeek(mondayStr));
  }, [currentMonday]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Clear message after 3 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Save week theme
  const saveWeekTheme = async (themeText) => {
    const mondayStr = dateToLocalString(currentMonday);
    await dataStore.setDailyNote(mondayStr, themeText, 'week_theme', currentUser);
    setWeekTheme(themeText);
    setEditingTheme(false);
    if (themeText) {
      setMessage('Week theme saved!');
    } else {
      setMessage('Week theme cleared');
    }
  };

  // Room notes functions
  const addRoomNote = async (roomId, dateStr) => {
    if (!newNoteText.trim()) return;
    await dataStore.addRoomDayNote(roomId, dateStr, newNoteText, currentUser);
    setNewNoteText('');
    setEditingRoomNote(null);
    loadData();
    setMessage('Note added!');
  };

  const deleteRoomNote = async (roomId, dateStr, noteId) => {
    if (window.confirm('Delete this note?')) {
      await dataStore.deleteRoomDayNote(roomId, dateStr, noteId, currentUser);
      loadData();
      setMessage('Note deleted');
    }
  };

  const getRoomNotesCount = (roomId, dateStr) => {
    const key = `${dateStr}_${roomId}`;
    return roomNotes[key]?.length || 0;
  };

  // Announcement handlers
  const openAddAnnouncement = () => {
    const mondayStr = dateToLocalString(currentMonday);
    setAnnouncementForm({ message: '', icon: 'ℹ️', date: mondayStr });
    setEditingAnnouncement(null);
    setShowAnnouncementModal(true);
  };

  const openEditAnnouncement = (ann) => {
    setAnnouncementForm({ message: ann.message, icon: ann.icon, date: ann.date });
    setEditingAnnouncement(ann);
    setShowAnnouncementModal(true);
  };

  const saveAnnouncement = () => {
    if (!announcementForm.message.trim()) {
      setMessage('Please enter an announcement message');
      return;
    }
    
    if (editingAnnouncement) {
      dataStore.updateAnnouncement(editingAnnouncement.id, announcementForm, currentUser);
      setMessage('Announcement updated');
    } else {
      dataStore.addAnnouncement(announcementForm, currentUser);
      setMessage('Announcement added');
    }
    
    setShowAnnouncementModal(false);
    loadData();
  };

  const deleteAnnouncement = (id) => {
    if (window.confirm('Delete this announcement?')) {
      dataStore.deleteAnnouncement(id, currentUser);
      setMessage('Announcement deleted');
      loadData();
    }
  };

  // Week navigation
  const prevWeek = () => {
    const newMonday = new Date(currentMonday);
    newMonday.setDate(currentMonday.getDate() - 7);
    setCurrentMonday(newMonday);
    setSelectedCell(null);
  };

  const nextWeek = () => {
    const newMonday = new Date(currentMonday);
    newMonday.setDate(currentMonday.getDate() + 7);
    setCurrentMonday(newMonday);
    setSelectedCell(null);
  };

  const goToCurrentWeek = () => {
    setCurrentMonday(getMonday(new Date()));
    setSelectedCell(null);
  };

  // Print handler - open print modal
  const handlePrint = () => {
    setShowPrintModal(true);
  };

  // Toggle publish status for the week
  const handleTogglePublish = async () => {
    try {
      const mondayStr = dateToLocalString(currentMonday);
      const newStatus = !isWeekPublished;
      await dataStore.setWeekPublished(mondayStr, newStatus, currentUser);
      setIsWeekPublished(newStatus);
      setMessage(newStatus ? 'Schedule published! Teachers can now view this week.' : 'Schedule unpublished. Teachers can no longer view this week.');
    } catch (err) {
      console.error('Error toggling publish status:', err);
      setMessage('Error: ' + err.message);
    }
  };

  // Create template handler
  const handleCreateTemplate = async () => {
    if (!templateName.trim()) {
      setMessage('Please enter a template name');
      return;
    }
    
    try {
      const mondayStr = dateToLocalString(currentMonday);
      const template = await dataStore.createTemplateFromWeek(
        mondayStr,
        templateName.trim(),
        templateDescription.trim(),
        currentUser
      );
      
      const stats = dataStore.getTemplateStats(template.id);
      if (stats.totalAssignments === 0) {
        setMessage('No schedule assignments found for this week');
        await dataStore.deleteTemplate(template.id, currentUser);
        return;
      }
      
      setMessage(`Template "${template.name}" created with ${stats.totalAssignments} assignments!`);
      setShowCreateTemplateModal(false);
      setTemplateName('');
      setTemplateDescription('');
    } catch (err) {
      console.error('Error creating template:', err);
      setMessage('Error: ' + (err.message || 'Failed to create template'));
    }
  };

  // Open apply template modal
  const openApplyTemplateModal = () => {
    const allTemplates = dataStore.getTemplates();
    setTemplates(allTemplates);
    setSelectedTemplateId(allTemplates.length > 0 ? allTemplates[0].id : '');
    setApplyOptions({ skipPTO: true, overwriteExisting: false, respectAvailability: true });
    setShowApplyTemplateModal(true);
  };

  // Apply template handler
  const handleApplyTemplate = async () => {
    if (!selectedTemplateId) {
      setMessage('Please select a template');
      return;
    }
    
    try {
      const mondayStr = dateToLocalString(currentMonday);
      const result = await dataStore.applyTemplate(
        selectedTemplateId,
        mondayStr,
        applyOptions,
        currentUser
      );
      
      if (result.success) {
        setMessage(`Applied ${result.createdCount} assignments (${result.skippedCount} skipped)`);
        setShowApplyTemplateModal(false);
        loadData(); // Refresh the schedule
      } else {
        setMessage('Error: ' + (result.error || 'Failed to apply template'));
      }
    } catch (err) {
      console.error('Error applying template:', err);
      setMessage('Error: ' + (err.message || 'Failed to apply template'));
    }
  };

  // Clear all teacher assignments for the week
  const clearWeekSchedule = async () => {
    const dates = getWeekDates();
    const startDate = dateToString(dates[0]);
    const endDate = dateToString(dates[dates.length - 1]);
    
    if (window.confirm(`Clear ALL teacher assignments from ${startDate} to ${endDate}?\n\nThis cannot be undone.`)) {
      for (const date of dates) {
        const dateStr = dateToString(date);
        // Use clearScheduleForDay which logs a single SCHEDULE_CLEARED audit entry per day
        await dataStore.clearScheduleForDay(dateStr, currentUser);
      }
      loadData();
      setMessage('Week schedule cleared');
    }
  };

  // Format week label for print header
  const formatPrintWeekLabel = () => {
    const dates = getWeekDates();
    const startDate = dates[0];
    const endDate = dates[dates.length - 1];
    const startStr = startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    const endStr = endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  };

  // Check if viewing current week
  const isCurrentWeek = () => {
    const today = getMonday(new Date());
    return currentMonday.getTime() === today.getTime();
  };

  // Format helpers
  const formatWeekLabel = () => {
    const dates = getWeekDates();
    const startDate = dates[0];
    const endDate = dates[dates.length - 1];
    const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${startStr} - ${endStr}`;
  };

  const formatDayName = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };

  const formatDayDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Format time from 24h to 12h format
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const dateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get assigned users for a cell (now returns assignment objects with time info)
  const getAssignedUsers = (dateStr, roomId) => {
    const daySchedule = schedule[dateStr] || {};
    const assignments = daySchedule[roomId] || [];
    return assignments.map(assignment => {
      const user = users.find(u => u.id === assignment.userId);
      if (user) {
        return {
          ...assignment,
          user: user
        };
      }
      return null;
    }).filter(Boolean);
  };

  // Check if user is available on date (partial day PTO still allows scheduling for remaining hours)
  const isUserAvailable = (userId, dateStr) => {
    // Only block if user has FULL day PTO, not partial
    if (dataStore.hasFullDayPTO(userId, dateStr)) return false;
    return dataStore.isUserAvailable(userId, dateStr);
  };

  // Check if user is already assigned anywhere on this date (no longer used to filter)
  const isUserAssignedOnDate = (userId, dateStr) => {
    const daySchedule = schedule[dateStr] || {};
    for (const roomId in daySchedule) {
      const assignments = daySchedule[roomId] || [];
      if (assignments.some(a => a.userId === userId)) return true;
    }
    return false;
  };

  // Get all assignments for a user on a specific date
  const getUserAssignmentsOnDate = (userId, dateStr) => {
    const daySchedule = schedule[dateStr] || {};
    const assignments = [];
    for (const roomId in daySchedule) {
      const roomAssignments = daySchedule[roomId] || [];
      const room = rooms.find(r => r.id === roomId);
      roomAssignments.forEach(a => {
        if (a.userId === userId) {
          assignments.push({ ...a, roomId, roomName: room?.shortName || 'Unknown' });
        }
      });
    }
    return assignments.sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  // Calculate available time slots for a user on a date
  // Returns array of { start, end } objects representing free time windows
  // Handles multiple availability slots (e.g., split shifts: before 9:30 + after 1:30)
  // Also accounts for partial day PTO as blocked time
  const getAvailableTimeSlots = (userId, dateStr) => {
    const userAvail = dataStore.getUserAvailabilityForDate(userId, dateStr);
    if (!userAvail || !userAvail.available) return [];
    
    // Get all availability slots for this day
    const availSlots = userAvail.slots || [];
    if (availSlots.length === 0) {
      // Fallback to old format
      const start = userAvail.start || '08:00';
      const end = userAvail.end || '17:00';
      availSlots.push({ start, end });
    }
    
    const existingAssignments = getUserAssignmentsOnDate(userId, dateStr);
    
    // Check for partial day PTO and treat it as a "blocked" assignment
    const ptoDetails = dataStore.getPTODetailsForDate(userId, dateStr);
    const blockedTimes = [...existingAssignments];
    
    if (ptoDetails && ptoDetails.isPartialDay) {
      // Add PTO time as a blocked slot
      blockedTimes.push({
        startTime: ptoDetails.startTime,
        endTime: ptoDetails.endTime,
        isPTO: true
      });
    }
    
    // Convert time string to minutes for easier math
    const timeToMinutes = (t) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    
    // Convert minutes back to time string
    const minutesToTime = (mins) => {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    };
    
    // Sort blocked times by start time
    const sortedBlockedTimes = [...blockedTimes].sort((a, b) => 
      timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
    );
    
    // Calculate free slots within EACH availability window
    const freeSlots = [];
    
    for (const availSlot of availSlots) {
      const availStartMins = timeToMinutes(availSlot.start);
      const availEndMins = timeToMinutes(availSlot.end);
      
      if (sortedBlockedTimes.length === 0) {
        // No blocked times - this entire availability window is free
        freeSlots.push({ start: availSlot.start, end: availSlot.end });
        continue;
      }
      
      let currentStart = availStartMins;
      
      for (const blocked of sortedBlockedTimes) {
        const blockStart = timeToMinutes(blocked.startTime);
        const blockEnd = timeToMinutes(blocked.endTime);
        
        // Skip blocked times outside this availability window
        if (blockEnd <= availStartMins || blockStart >= availEndMins) {
          continue;
        }
        
        // If there's a gap before this blocked time (within this window), add it as a slot
        const effectiveBlockStart = Math.max(blockStart, availStartMins);
        if (effectiveBlockStart > currentStart) {
          freeSlots.push({
            start: minutesToTime(currentStart),
            end: minutesToTime(effectiveBlockStart)
          });
        }
        
        // Move current start to after this blocked time (but not beyond this window)
        currentStart = Math.max(currentStart, Math.min(blockEnd, availEndMins));
      }
      
      // If there's time left in this window after all blocked times, add final slot
      if (currentStart < availEndMins) {
        freeSlots.push({
          start: minutesToTime(currentStart),
          end: minutesToTime(availEndMins)
        });
      }
    }
    
    return freeSlots;
  };

  // Check if a time range overlaps with existing assignments
  const hasTimeOverlap = (userId, dateStr, startTime, endTime) => {
    const existingAssignments = getUserAssignmentsOnDate(userId, dateStr);
    
    const timeToMinutes = (t) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    
    const newStart = timeToMinutes(startTime);
    const newEnd = timeToMinutes(endTime);
    
    for (const assignment of existingAssignments) {
      const existStart = timeToMinutes(assignment.startTime);
      const existEnd = timeToMinutes(assignment.endTime);
      
      // Check for overlap: new range starts before existing ends AND new range ends after existing starts
      if (newStart < existEnd && newEnd > existStart) {
        return true;
      }
    }
    return false;
  };

  // Get available users for a date (now includes all available users, even if partially assigned)
  const getAvailableUnassignedUsers = (dateStr) => {
    return users.filter(user => 
      isUserAvailable(user.id, dateStr) && 
      !isUserAssignedOnDate(user.id, dateStr)
    );
  };

  // Get ALL available users for a date (for assignment modal - allows multiple assignments)
  const getAllAvailableUsers = (dateStr) => {
    return users.filter(user => isUserAvailable(user.id, dateStr));
  };

  // Get users who have approved PTO for a specific date (with partial day info)
  const getUsersOnPTO = (dateStr) => {
    return users.filter(user => dataStore.hasApprovedPTO(user.id, dateStr)).map(user => ({
      ...user,
      ptoDetails: dataStore.getPTODetailsForDate(user.id, dateStr)
    }));
  };

  // Check if user has full day PTO (completely unavailable)
  const hasFullDayPTO = (userId, dateStr) => {
    return dataStore.hasFullDayPTO(userId, dateStr);
  };

  // Assign user to room with time slot
  const handleAssign = async (userId, dateStr, roomId, startTime, endTime) => {
    try {
      await dataStore.assignUserToRoom(dateStr, roomId, userId, startTime, endTime, currentUser);
      
      // Refresh schedule from cache (which was updated by dataStore)
      const mondayStr = dateToLocalString(currentMonday);
      setSchedule(dataStore.getWeekSchedule(mondayStr));
      
      setMessage('Teacher assigned successfully!');
      setSelectedCell(null);
      setAssignmentTimes({ startTime: '08:00', endTime: '17:00' });
    } catch (err) {
      console.error('Error assigning teacher:', err);
      setMessage('Error: ' + err.message);
    }
  };

  // Remove assignment from room
  const handleUnassign = async (assignmentId, dateStr, roomId) => {
    try {
      await dataStore.removeUserFromRoom(dateStr, roomId, assignmentId, currentUser);
      
      // Refresh schedule from cache (which was updated by dataStore)
      const mondayStr = dateToLocalString(currentMonday);
      setSchedule(dataStore.getWeekSchedule(mondayStr));
      
      setMessage('Teacher removed from schedule.');
    } catch (err) {
      console.error('Error removing teacher:', err);
      setMessage('Error: ' + err.message);
    }
  };

  // Open edit modal for an existing assignment
  const handleEditAssignment = (assignment, dateStr, roomId) => {
    setEditingAssignment({
      assignmentId: assignment.id,
      userId: assignment.userId,
      userName: assignment.user?.name || 'Teacher',
      roomId: roomId,
      roomName: rooms.find(r => r.id === roomId)?.name || 'Room',
      dateStr: dateStr,
      startTime: assignment.startTime,
      endTime: assignment.endTime
    });
  };

  // Save edited assignment times
  const handleSaveEditedAssignment = async () => {
    if (!editingAssignment) return;
    
    try {
      const { assignmentId, userId, roomId, dateStr, startTime, endTime } = editingAssignment;
      
      // Remove old assignment and create new one with updated times
      await dataStore.removeUserFromRoom(dateStr, roomId, assignmentId, currentUser);
      await dataStore.assignUserToRoom(dateStr, roomId, userId, startTime, endTime, currentUser);
      
      loadData();
      setMessage('Assignment time updated.');
      setEditingAssignment(null);
    } catch (err) {
      console.error('Error updating assignment:', err);
      setMessage('Error: ' + err.message);
    }
  };

  // Handle assign button click - open modal
  const handleAssignClick = (e, dateStr, roomId) => {
    e.stopPropagation();
    
    // Initialize per-user times based on their first available slot
    const availableUsers = users.filter(user => isUserAvailable(user.id, dateStr));
    const userTimes = {};
    availableUsers.forEach(user => {
      const slots = getAvailableTimeSlots(user.id, dateStr);
      if (slots.length > 0) {
        // Use the first available slot (or longest slot)
        const longestSlot = slots.reduce((longest, slot) => {
          const slotDuration = timeToMinutes(slot.end) - timeToMinutes(slot.start);
          const longestDuration = timeToMinutes(longest.end) - timeToMinutes(longest.start);
          return slotDuration > longestDuration ? slot : longest;
        }, slots[0]);
        userTimes[user.id] = { start: longestSlot.start, end: longestSlot.end };
      } else {
        // No available slots - use their full availability (will show warning)
        const userAvail = dataStore.getUserAvailabilityForDate(user.id, dateStr);
        userTimes[user.id] = { start: userAvail?.start || '08:00', end: userAvail?.end || '17:00' };
      }
    });
    
    setModalUserTimes(userTimes);
    setSelectedCell({ date: dateStr, roomId });
  };
  
  // Helper for time calculations
  const timeToMinutes = (t) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  // Total scheduled hours for a teacher across the currently visible week
  // Each shift has its unpaid lunch break deducted before being summed
  const getUserWeeklyHours = (userId) => {
    let totalMinutes = 0;
    Object.values(schedule).forEach(daySchedule => {
      Object.values(daySchedule).forEach(roomAssignments => {
        roomAssignments.forEach(assignment => {
          if (assignment.userId === userId) {
            const shiftMinutes = timeToMinutes(assignment.endTime) - timeToMinutes(assignment.startTime);
            totalMinutes += Math.max(0, shiftMinutes - LUNCH_BREAK_MINUTES);
          }
        });
      });
    });
    return totalMinutes / 60;
  };

  // Drag and drop handlers
  const handleDragStart = (e, userId, sourceRoomId = null, sourceDateStr = null, assignmentId = null) => {
    setDraggedItem({ userId, sourceRoomId, sourceDateStr, assignmentId });
    e.dataTransfer.setData('userId', userId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverCell(null);
  };

  const handleDragOver = (e, roomId, dateStr) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const cellKey = `${roomId}-${dateStr}`;
    if (dragOverCell !== cellKey) {
      setDragOverCell(cellKey);
    }
  };

  const handleDragLeave = () => {
    setDragOverCell(null);
  };

  const handleDrop = async (e, targetRoomId, dateStr) => {
    e.preventDefault();
    setDragOverCell(null);
    
    const userId = e.dataTransfer.getData('userId');
    if (!userId || !isUserAvailable(userId, dateStr)) {
      setDraggedItem(null);
      return;
    }
    
    // If dragging from an assigned position (has sourceRoomId), this is a move/copy operation
    if (draggedItem && draggedItem.sourceRoomId && draggedItem.assignmentId) {
      const { sourceRoomId, sourceDateStr, assignmentId } = draggedItem;
      
      // Don't do anything if dropping in same cell
      if (sourceRoomId === targetRoomId && sourceDateStr === dateStr) {
        setDraggedItem(null);
        return;
      }
      
      // Get the original assignment's time slot
      const daySchedule = schedule[sourceDateStr] || {};
      const roomAssignments = daySchedule[sourceRoomId] || [];
      const originalAssignment = roomAssignments.find(a => a.id === assignmentId);
      const originalStartTime = originalAssignment?.startTime || '08:00';
      const originalEndTime = originalAssignment?.endTime || '17:00';
      
      // Determine if this is a COPY (different day) or MOVE (same day, different room)
      const isDifferentDay = sourceDateStr !== dateStr;
      
      if (isDifferentDay) {
        // COPY operation - dragging to a different day
        // Check for time conflicts on target day
        const existingAssignments = getUserAssignmentsOnDate(userId, dateStr);
        const hasConflict = existingAssignments.some(existing => {
          const existStart = timeToMinutes(existing.startTime);
          const existEnd = timeToMinutes(existing.endTime);
          const newStart = timeToMinutes(originalStartTime);
          const newEnd = timeToMinutes(originalEndTime);
          return newStart < existEnd && newEnd > existStart;
        });
        
        if (hasConflict) {
          const user = users.find(u => u.id === userId);
          setMessage(`Cannot copy: ${user?.name || 'Teacher'} already has a conflicting assignment on that day`);
          setDraggedItem(null);
          return;
        }
        
        try {
          const user = users.find(u => u.id === userId);
          const room = rooms.find(r => r.id === targetRoomId);
          
          await dataStore.assignUserToRoom(dateStr, targetRoomId, userId, originalStartTime, originalEndTime, currentUser);
          
          // Refresh schedule from cache (which was updated by dataStore)
          const mondayStr = dateToLocalString(currentMonday);
          setSchedule(dataStore.getWeekSchedule(mondayStr));
          
          setMessage(`Copied ${user?.name || 'teacher'} to ${room?.shortName || 'room'} on ${new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' })}`);
        } catch (err) {
          console.error('Error copying assignment:', err);
          setMessage('Error: ' + err.message);
        }
      } else {
        // MOVE operation - same day, different room
        // Check for time conflicts with OTHER assignments (not the one being moved)
        const existingAssignments = getUserAssignmentsOnDate(userId, dateStr);
        const otherAssignments = existingAssignments.filter(a => a.id !== assignmentId);
        const hasConflict = otherAssignments.some(existing => {
          const existStart = timeToMinutes(existing.startTime);
          const existEnd = timeToMinutes(existing.endTime);
          const newStart = timeToMinutes(originalStartTime);
          const newEnd = timeToMinutes(originalEndTime);
          return newStart < existEnd && newEnd > existStart;
        });
        
        if (hasConflict) {
          const user = users.find(u => u.id === userId);
          setMessage(`Cannot move: ${user?.name || 'Teacher'} has a conflicting assignment in another room`);
          setDraggedItem(null);
          return;
        }
        
        try {
          const user = users.find(u => u.id === userId);
          const room = rooms.find(r => r.id === targetRoomId);
          
          // Remove from source, add to target
          await dataStore.removeUserFromRoom(sourceDateStr, sourceRoomId, assignmentId, currentUser);
          await dataStore.assignUserToRoom(dateStr, targetRoomId, userId, originalStartTime, originalEndTime, currentUser);
          
          // Refresh schedule from cache (which was updated by dataStore)
          const mondayStr = dateToLocalString(currentMonday);
          setSchedule(dataStore.getWeekSchedule(mondayStr));
          
          setMessage(`Moved ${user?.name || 'teacher'} to ${room?.shortName || 'room'}`);
        } catch (err) {
          console.error('Error moving assignment:', err);
          setMessage('Error: ' + err.message);
        }
      }
    } else {
      // Dragging from available section - use user's full availability time
      const userAvail = dataStore.getUserAvailabilityForDate(userId, dateStr);
      const startTime = userAvail?.start || '08:00';
      const endTime = userAvail?.end || '17:00';
      await handleAssign(userId, dateStr, targetRoomId, startTime, endTime);
    }
    
    setDraggedItem(null);
  };

  const weekDates = getWeekDates();

  // =============================================
  // STYLES
  // =============================================
  const containerStyle = {
    padding: '20px',
    maxWidth: '1400px',
    margin: '0 auto'
  };

  // Week navigation sub-header
  const weekNavStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    padding: '12px 20px',
    backgroundColor: theme.cardBackground,
    borderRadius: '8px',
    border: `1px solid ${theme.border}`
  };

  const navButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: colors.blue,
    color: darkMode ? '#fff' : '#333',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '600',
    transition: 'all 0.2s ease'
  };

  const weekTitleStyle = {
    fontSize: '1rem',
    fontWeight: '600',
    color: theme.text
  };

  // Schedule grid styles
  const gridContainerStyle = {
    backgroundColor: theme.cardBackground,
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    overflow: 'hidden'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '140px repeat(5, minmax(180px, 1fr))',
    gap: '1px',
    backgroundColor: theme.border
  };

  const headerCellStyle = {
    padding: '16px 12px',
    backgroundColor: theme.backgroundSecondary,
    textAlign: 'center'
  };

  const roomHeaderStyle = {
    padding: '16px 12px',
    backgroundColor: colors.blue,
    color: darkMode ? '#fff' : '#333',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '0.95rem'
  };

  const dayNameStyle = {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: theme.text,
    marginBottom: '4px'
  };

  const dayDateStyle = {
    fontSize: '0.8rem',
    color: theme.textSecondary
  };

  const roomLabelStyle = {
    padding: '12px',
    backgroundColor: theme.backgroundSecondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const roomCardStyle = (index) => {
    const bgColors = [
      darkMode ? '#3d5a4a' : '#e8f5e9',  // Green tint
      darkMode ? '#4a5a3d' : '#f1f8e9',  // Light green
      darkMode ? '#3d4a5a' : '#e3f2fd',  // Blue tint
      darkMode ? '#5a4a3d' : '#fff8e1'   // Amber tint
    ];
    return {
      padding: '10px 14px',
      backgroundColor: bgColors[index % bgColors.length],
      borderRadius: '8px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
      width: '100%',
      boxSizing: 'border-box',
      textAlign: 'center'
    };
  };

  const roomNameStyle = {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: theme.text
  };

  const roomInfoStyle = {
    fontSize: '0.7rem',
    color: theme.textSecondary
  };

  // Cell styles - now a function for drag feedback
  const getCellStyle = (roomId, dateStr) => ({
    padding: '12px',
    backgroundColor: dragOverCell === `${roomId}-${dateStr}` 
      ? (darkMode ? 'rgba(119,184,219,0.3)' : 'rgba(119,184,219,0.2)') 
      : theme.cardBackground,
    minHeight: '80px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    overflow: 'hidden',
    transition: 'background-color 0.15s ease',
    border: dragOverCell === `${roomId}-${dateStr}` ? `2px dashed ${colors.blue}` : '2px solid transparent'
  });

  // Assign button style - hover handled by CSS-in-JS events on the button itself
  const assignButtonBaseStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '8px 16px',
    border: `1px dashed ${theme.border}`,
    borderRadius: '6px',
    backgroundColor: 'transparent',
    color: theme.textSecondary,
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    width: '100%'
  };

  const assignButtonHoverStyle = {
    ...assignButtonBaseStyle,
    border: `1px dashed ${colors.blue}`,
    backgroundColor: darkMode ? 'rgba(119,184,219,0.15)' : 'rgba(119,184,219,0.1)',
    color: colors.blue
  };

  // Teacher tag in cell - now a function for drag state
  const getTeacherTagStyle = (isDragging, isOvertime) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: isOvertime
      ? (darkMode ? 'rgba(239,83,80,0.5)' : 'rgba(239,83,80,0.6)')
      : colors.blue,
    border: `2px solid ${isOvertime ? colors.red : 'transparent'}`,
    borderRadius: '6px',
    color: '#333',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'grab',
    opacity: isDragging ? 0.5 : 1,
    userSelect: 'none'
  });

  const removeButtonStyle = {
    background: 'none',
    border: 'none',
    color: '#333',
    cursor: 'pointer',
    fontSize: '1.2rem',
    padding: '0 0 0 8px',
    opacity: 0.8,
    transition: 'opacity 0.2s'
  };

  // Available teachers footer
  const footerStyle = {
    display: 'grid',
    gridTemplateColumns: '140px repeat(5, minmax(180px, 1fr))',
    gap: '1px',
    backgroundColor: theme.border,
    borderTop: `2px solid ${colors.green}`
  };

  const footerLabelStyle = {
    padding: '12px',
    backgroundColor: theme.backgroundSecondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '0.85rem',
    color: theme.text
  };

  const footerCellStyle = {
    padding: '12px',
    backgroundColor: theme.cardBackground,
    minHeight: '60px'
  };

  const availableTagStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 12px',
    margin: '2px',
    backgroundColor: colors.green,
    borderRadius: '4px',
    color: '#333',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'grab',
    flexWrap: 'wrap'
  };

  const noAvailabilityStyle = {
    color: theme.textSecondary,
    fontSize: '0.8rem',
    fontStyle: 'italic'
  };

  // PTO row styles
  const ptoFooterStyle = {
    display: 'grid',
    gridTemplateColumns: '140px repeat(5, minmax(180px, 1fr))',
    gap: '1px',
    backgroundColor: theme.border,
    borderTop: `2px solid ${colors.red}`
  };

  const ptoTagStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 12px',
    margin: '2px',
    backgroundColor: colors.red,
    borderRadius: '4px',
    color: '#333',
    fontSize: '0.9rem',
    fontWeight: '600'
  };

  // Partial day PTO style - uses orange/amber with triangle indicator
  const partialPtoTagStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 12px',
    margin: '2px',
    backgroundColor: '#f59e0b', // amber/orange
    borderRadius: '4px',
    color: '#333',
    fontSize: '0.85rem',
    fontWeight: '600',
    position: 'relative',
    overflow: 'hidden'
  };

  // Assignment modal
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  };

  const modalStyle = {
    backgroundColor: theme.cardBackground,
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '400px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
  };

  const modalTitleStyle = {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: theme.text,
    marginBottom: '8px'
  };

  const modalSubtitleStyle = {
    fontSize: '0.85rem',
    color: theme.textSecondary,
    marginBottom: '16px'
  };

  const modalTeacherBtnStyle = {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    marginBottom: '8px',
    border: `1px solid ${theme.border}`,
    borderRadius: '8px',
    backgroundColor: theme.background,
    color: theme.text,
    cursor: 'pointer',
    fontSize: '0.9rem',
    textAlign: 'left',
    transition: 'all 0.2s ease'
  };

  const modalCancelBtnStyle = {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    marginTop: '12px',
    border: `1px solid ${theme.border}`,
    borderRadius: '8px',
    backgroundColor: darkMode ? theme.backgroundSecondary : '#e5e7eb',
    color: theme.text,
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'all 0.2s ease'
  };

  // Message toast
  const messageStyle = {
    position: 'fixed',
    top: '80px',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '12px 24px',
    borderRadius: '8px',
    backgroundColor: '#f59e0b',
    color: '#000',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    zIndex: 1001,
    fontSize: '0.9rem',
    fontWeight: '600'
  };

  // =============================================
  // RENDER
  // =============================================
  return (
    <div style={containerStyle} className="print-container">
      {/* Print Header - Only shows when printing */}
      <div className="print-header">
        <h1>Weekly Schedule</h1>
        <p>{formatPrintWeekLabel()}</p>
      </div>

      {/* Print Announcements - Only shows when printing */}
      <div className="print-announcements">
        <div className="print-announcements-title">Announcements</div>
        {announcements.length > 0 ? (
          announcements.map(ann => (
            <div key={ann.id} className="print-announcement-item">
              {new Date(ann.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}: {ann.message}
            </div>
          ))
        ) : (
          <div className="print-announcement-item">No announcements this week</div>
        )}
      </div>

      {/* Header Card with Week Theme - Hidden when printing */}
      <div style={{
        marginBottom: '16px',
        padding: '16px 20px',
        backgroundColor: theme.cardBackground,
        borderRadius: '8px',
        border: `1px solid ${theme.border}`
      }} className="no-print">
        {/* Top Row: Title + Legend + Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
          paddingBottom: '12px',
          borderBottom: `1px solid ${theme.border}`
        }}>
          <h1 style={{ fontSize: '1.4rem', fontWeight: '700', color: theme.text, margin: 0 }}>📋 Teacher Schedule</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            {/* Legend */}
            <div style={{ display: 'flex', gap: '12px', fontSize: '0.8rem', color: theme.text }}>
              <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: colors.green, borderRadius: '3px', marginRight: '6px', verticalAlign: 'middle' }}></span>Valid</span>
              <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: colors.orange, borderRadius: '3px', marginRight: '6px', verticalAlign: 'middle' }}></span>Coverage Gap</span>
              <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: colors.red, borderRadius: '3px', marginRight: '6px', verticalAlign: 'middle' }}></span>Ratio Exceeded</span>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                style={{
                  ...navButtonStyle,
                  backgroundColor: colors.red,
                  color: '#333',
                  border: 'none'
                }}
                onClick={clearWeekSchedule}
              >
                Clear All
              </button>
              <button 
                style={{
                  ...navButtonStyle,
                  backgroundColor: colors.blue,
                  color: '#333',
                  border: 'none'
                }}
                onClick={handlePrint}
              >
                🖨️ Print
              </button>
              <button 
                style={{
                  ...navButtonStyle,
                  backgroundColor: isWeekPublished ? colors.orange : '#22d3d1',
                  color: '#333',
                  border: 'none'
                }}
                onClick={handleTogglePublish}
                title={isWeekPublished ? 'Unpublish - hide schedule from teachers' : 'Publish - make schedule visible to teachers'}
              >
                {isWeekPublished ? '📤 Unpublish' : '📢 Publish'}
              </button>
              <button 
                style={{
                  ...navButtonStyle,
                  backgroundColor: colors.green,
                  color: '#333',
                  border: 'none'
                }}
                onClick={() => setShowCreateTemplateModal(true)}
                title="Save this week's schedule as a reusable template"
              >
                📋 Save as Template
              </button>
              <button 
                style={{
                  ...navButtonStyle,
                  backgroundColor: colors.purple || '#9333ea',
                  color: '#333',
                  border: 'none'
                }}
                onClick={openApplyTemplateModal}
                title="Apply a saved template to this week"
              >
                📥 Apply Template
              </button>
            </div>
          </div>
        </div>
        
        {/* Bottom Row: Week Theme + Announcements side by side */}
        <div style={{ 
          display: 'flex', 
          gap: '16px',
          flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
        }}>
          {/* Week Theme Card */}
          <div style={{
            flex: 1,
            padding: '12px 16px',
            backgroundColor: theme.backgroundSecondary,
            borderRadius: '8px',
            border: `1px solid ${theme.border}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <span style={{ fontSize: '1rem' }}>🎨</span>
              <span style={{ fontWeight: '600', color: theme.text, fontSize: '0.9rem' }}>Week Theme</span>
            </div>
            
            {editingTheme ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="text"
                  value={weekTheme}
                  onChange={(e) => setWeekTheme(e.target.value)}
                  placeholder="e.g., Pajama Week, Ocean Animals..."
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    borderRadius: '4px',
                    border: `1px solid ${theme.border}`,
                    backgroundColor: theme.cardBackground,
                    color: theme.text,
                    fontSize: '0.85rem'
                  }}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveWeekTheme(weekTheme);
                    if (e.key === 'Escape') { setEditingTheme(false); loadData(); }
                  }}
                />
                <button
                  onClick={() => saveWeekTheme(weekTheme)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: colors.green,
                    color: '#333',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.8rem'
                  }}
                >
                  Save
                </button>
                <button
                  onClick={() => { setEditingTheme(false); loadData(); }}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: 'transparent',
                    color: theme.textSecondary,
                    border: `1px solid ${theme.border}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ 
                  color: weekTheme ? theme.text : theme.textSecondary, 
                  fontStyle: weekTheme ? 'normal' : 'italic',
                  fontSize: '0.9rem'
                }}>
                  {weekTheme || 'No theme set'}
                </span>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={() => setEditingTheme(true)}
                    style={{
                      padding: '4px 10px',
                      backgroundColor: 'transparent',
                      color: colors.blue,
                      border: `1px solid ${colors.blue}`,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.75rem'
                    }}
                  >
                    {weekTheme ? 'Edit' : 'Set'}
                  </button>
                  {weekTheme && (
                    <button
                      onClick={() => saveWeekTheme('')}
                      style={{
                        padding: '4px 10px',
                        backgroundColor: 'transparent',
                        color: colors.red,
                        border: `1px solid ${colors.red}`,
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Announcements Card */}
          <div style={{
            flex: 1,
            padding: '12px 16px',
            backgroundColor: theme.backgroundSecondary,
            borderRadius: '8px',
            border: `1px solid ${theme.border}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1rem' }}>📢</span>
                <span style={{ fontWeight: '600', color: theme.text, fontSize: '0.9rem' }}>Announcements</span>
                {announcements.length > 0 && (
                  <span style={{
                    fontSize: '0.7rem',
                    padding: '1px 6px',
                    backgroundColor: darkMode ? 'rgba(119,184,219,0.2)' : 'rgba(119,184,219,0.15)',
                    borderRadius: '8px',
                    color: colors.blue
                  }}>
                    {announcements.length}
                  </span>
                )}
              </div>
              <button
                onClick={openAddAnnouncement}
                style={{
                  padding: '4px 10px',
                  backgroundColor: colors.blue,
                  color: '#333',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}
              >
                + Add
              </button>
            </div>
            
            {announcements.length === 0 ? (
              <p style={{ 
                color: theme.textSecondary, 
                fontStyle: 'italic', 
                margin: 0,
                fontSize: '0.85rem'
              }}>
                No announcements this week
              </p>
            ) : (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '6px',
                maxHeight: '100px',
                overflowY: 'auto'
              }}>
                {announcements.map(ann => (
                  <div key={ann.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 10px',
                    backgroundColor: theme.cardBackground,
                    borderRadius: '4px',
                    border: `1px solid ${theme.border}`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: '0.9rem' }}>{ann.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          color: theme.text, 
                          fontSize: '0.85rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>{ann.message}</div>
                        <div style={{ color: theme.textSecondary, fontSize: '0.7rem' }}>
                          {new Date(ann.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                      <button
                        onClick={() => openEditAnnouncement(ann)}
                        style={{
                          padding: '2px 6px',
                          backgroundColor: 'transparent',
                          color: colors.blue,
                          border: `1px solid ${colors.blue}`,
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '0.7rem'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteAnnouncement(ann.id)}
                        style={{
                          padding: '2px 6px',
                          backgroundColor: 'transparent',
                          color: colors.red,
                          border: `1px solid ${colors.red}`,
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '0.7rem'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Week Navigation - Hidden when printing */}
      <div style={weekNavStyle} className="no-print">
        <button style={navButtonStyle} onClick={prevWeek}>
          ← Previous
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={weekTitleStyle}>{formatWeekLabel()}</span>
          {!isCurrentWeek() && (
            <button 
              style={{
                padding: '8px 16px',
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                backgroundColor: theme.backgroundSecondary,
                color: theme.text,
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: '600'
              }}
              onClick={goToCurrentWeek}
            >
              Today
            </button>
          )}
        </div>

        <button style={navButtonStyle} onClick={nextWeek}>
          Next →
        </button>
      </div>

      {/* Schedule Grid */}
      <div style={gridContainerStyle} className="print-grid-container">
        <div style={gridStyle} className="print-schedule-grid">
          {/* Header Row */}
          <div style={roomHeaderStyle} className="print-header-cell">
            Room
          </div>
          {weekDates.map(date => (
            <div key={dateToString(date)} style={headerCellStyle} className="print-header-cell">
              <div style={dayNameStyle}>{formatDayName(date)}</div>
              <div style={dayDateStyle}>{formatDayDate(date)}</div>
            </div>
          ))}
          
          {/* Room Rows */}
          {rooms.map((room, roomIndex) => (
            <div key={room.id} className="room-row-wrapper" style={{ display: 'contents' }}>
              {/* Room Label */}
              <div style={roomLabelStyle} className="print-room-cell">
                <div style={roomCardStyle(roomIndex)}>
                  <div style={roomNameStyle}>🏫 {room.shortName}</div>
                  {(room.ratio || room.ageRange) && (
                    <div style={roomInfoStyle}>
                      {room.ratio ? `${room.ratio}:1` : ''}{room.ratio && room.ageRange ? ' • ' : ''}{room.ageRange ? room.ageRange.displayText : ''}
                    </div>
                  )}
                </div>
              </div>

              {/* Day Cells */}
              {weekDates.map(date => {
                const dateStr = dateToString(date);
                const cellKey = `${dateStr}-${room.id}`;
                const assignedUsers = getAssignedUsers(dateStr, room.id);
                const ratioStatus = dataStore.calculateRoomRatio(room.id, dateStr);

                return (
                  <div
                    key={cellKey}
                    style={getCellStyle(room.id, dateStr)}
                    className="print-day-cell"
                    onDragOver={(e) => handleDragOver(e, room.id, dateStr)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, room.id, dateStr)}
                  >
                    {/* TOP: Action Buttons Row - Assign + Notes side by side */}
                    <div className="no-print" style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                      <button
                        style={{
                          ...assignButtonBaseStyle,
                          flex: 1,
                          marginTop: 0
                        }}
                        onClick={(e) => handleAssignClick(e, dateStr, room.id)}
                        onMouseEnter={(e) => {
                          e.target.style.border = `1px dashed ${colors.blue}`;
                          e.target.style.backgroundColor = darkMode ? 'rgba(119,184,219,0.15)' : 'rgba(119,184,219,0.1)';
                          e.target.style.color = colors.blue;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.border = `1px dashed ${theme.border}`;
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = theme.textSecondary;
                        }}
                      >
                        + Assign
                      </button>
                      <button
                        onClick={() => setEditingRoomNote({ dateStr, roomId: room.id })}
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          padding: '6px 8px',
                          backgroundColor: getRoomNotesCount(room.id, dateStr) > 0 
                            ? (darkMode ? 'rgba(119,184,219,0.2)' : 'rgba(119,184,219,0.15)')
                            : 'transparent',
                          border: `1px dashed ${getRoomNotesCount(room.id, dateStr) > 0 ? colors.blue : theme.border}`,
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          color: getRoomNotesCount(room.id, dateStr) > 0 ? colors.blue : theme.textSecondary
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.border = `1px dashed ${colors.blue}`;
                          e.currentTarget.style.backgroundColor = darkMode ? 'rgba(119,184,219,0.15)' : 'rgba(119,184,219,0.1)';
                          e.currentTarget.style.color = colors.blue;
                        }}
                        onMouseLeave={(e) => {
                          const hasNotes = getRoomNotesCount(room.id, dateStr) > 0;
                          e.currentTarget.style.border = `1px dashed ${hasNotes ? colors.blue : theme.border}`;
                          e.currentTarget.style.backgroundColor = hasNotes 
                            ? (darkMode ? 'rgba(119,184,219,0.2)' : 'rgba(119,184,219,0.15)')
                            : 'transparent';
                          e.currentTarget.style.color = hasNotes ? colors.blue : theme.textSecondary;
                        }}
                      >
                        📝 {getRoomNotesCount(room.id, dateStr) > 0 
                          ? getRoomNotesCount(room.id, dateStr)
                          : ''}
                      </button>
                    </div>
                    
                    {/* Notes Panel (when editing) */}
                    {editingRoomNote?.dateStr === dateStr && editingRoomNote?.roomId === room.id && (
                      <div className="no-print" style={{
                        marginBottom: '8px',
                        padding: '8px',
                        backgroundColor: theme.backgroundSecondary,
                        borderRadius: '6px',
                        border: `1px solid ${theme.border}`
                      }}>
                        {/* Existing notes */}
                        {roomNotes[`${dateStr}_${room.id}`]?.map(note => (
                          <div key={note.id} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '6px',
                            padding: '6px',
                            backgroundColor: theme.cardBackground,
                            borderRadius: '4px',
                            fontSize: '0.8rem'
                          }}>
                            <div style={{ flex: 1 }}>
                              <p style={{ margin: 0, color: theme.text }}>{note.text}</p>
                              <p style={{ margin: '2px 0 0 0', color: theme.textSecondary, fontSize: '0.7rem' }}>
                                — {note.createdByName}
                              </p>
                            </div>
                            <button
                              onClick={() => deleteRoomNote(room.id, dateStr, note.id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: colors.red,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                padding: '0 4px'
                              }}
                              title="Delete note"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        
                        {/* Add note input */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
                          <input
                            type="text"
                            value={newNoteText}
                            onChange={(e) => setNewNoteText(e.target.value)}
                            placeholder="Add a note..."
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              borderRadius: '4px',
                              border: `1px solid ${theme.border}`,
                              backgroundColor: theme.cardBackground,
                              color: theme.text,
                              fontSize: '0.8rem',
                              boxSizing: 'border-box'
                            }}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') addRoomNote(room.id, dateStr);
                              if (e.key === 'Escape') { setEditingRoomNote(null); setNewNoteText(''); }
                            }}
                          />
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={() => addRoomNote(room.id, dateStr)}
                              style={{
                                flex: 1,
                                padding: '6px 10px',
                                backgroundColor: colors.green,
                                color: darkMode ? '#fff' : '#333',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '0.85';
                                e.currentTarget.style.transform = 'scale(1.02)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '1';
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                            >
                              Add
                            </button>
                            <button
                              onClick={() => { setEditingRoomNote(null); setNewNoteText(''); }}
                              style={{
                                flex: 1,
                                padding: '6px 10px',
                                backgroundColor: 'transparent',
                                color: theme.textSecondary,
                                border: `1px solid ${theme.border}`,
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.border = `1px solid ${colors.blue}`;
                                e.currentTarget.style.color = colors.blue;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.border = `1px solid ${theme.border}`;
                                e.currentTarget.style.color = theme.textSecondary;
                              }}
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* MIDDLE: Ratio Indicator */}
                    {ratioStatus && !ratioStatus.noRatioSet && (ratioStatus.childCount > 0 || ratioStatus.teacherCount > 0) && (() => {
                      let statusColor, statusBg, statusIcon, statusTitle;
                      
                      if (!ratioStatus.isValid) {
                        statusColor = colors.red;
                        statusBg = darkMode ? 'rgba(239,83,80,0.3)' : 'rgba(239,83,80,0.4)';
                        statusIcon = '⚠️';
                        statusTitle = `RATIO EXCEEDED: ${ratioStatus.childCount} children with ${ratioStatus.teacherCount} teacher(s). Room requires ${room.ratio}:1 ratio`;
                      } else if (ratioStatus.hasCoverageGap) {
                        statusColor = colors.orange;
                        statusBg = darkMode ? 'rgba(255,183,77,0.3)' : 'rgba(255,183,77,0.4)';
                        statusIcon = '⏰';
                        statusTitle = `COVERAGE GAP: ${ratioStatus.coverageDetails}`;
                      } else {
                        statusColor = colors.green;
                        statusBg = darkMode ? 'rgba(129,199,132,0.3)' : 'rgba(129,199,132,0.4)';
                        statusIcon = '✓';
                        statusTitle = `${ratioStatus.childCount} children with ${ratioStatus.teacherCount} teacher(s). Room ratio: ${room.ratio}:1`;
                      }
                      
                      return (
                        <div 
                          className="no-print"
                          style={{
                            marginBottom: '8px',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            textAlign: 'center',
                            backgroundColor: statusBg,
                            color: darkMode ? '#fff' : '#333',
                            border: `1px solid ${statusColor}`
                          }}
                          title={statusTitle}
                        >
                          👶 {ratioStatus.childCount}
                        </div>
                      );
                    })()}

                    {/* BOTTOM: Assigned Teachers */}
                    {assignedUsers.map(assignment => {
                      const isDragging = draggedItem?.assignmentId === assignment.id;
                      const weeklyHours = getUserWeeklyHours(assignment.userId);
                      const isOvertime = weeklyHours > OVERTIME_HOURS_THRESHOLD;
                      const weeklyHoursLabel = Math.round(weeklyHours * 10) / 10;
                      return (
                        <div
                          key={assignment.id}
                          style={getTeacherTagStyle(isDragging, isOvertime)}
                          className="print-assignment"
                          draggable
                          onDragStart={(e) => handleDragStart(e, assignment.userId, room.id, dateStr, assignment.id)}
                          onDragEnd={handleDragEnd}
                          title={isOvertime
                            ? `⚠️ ${assignment.user.name} is scheduled ${weeklyHoursLabel}h this week (exceeds ${OVERTIME_HOURS_THRESHOLD}h)`
                            : `${assignment.user.name} - Click to edit time, drag to move/copy`}
                        >
                          <div
                            style={{ display: 'flex', flexDirection: 'column', gap: '2px', cursor: 'pointer', flex: 1 }}
                            onClick={() => handleEditAssignment(assignment, dateStr, room.id)}
                            className="no-print-click"
                          >
                            <span className="print-assignment-name">
                              {assignment.user.name}{isOvertime && ' ⚠️'}
                            </span>
                            <span style={{ fontSize: '0.8rem', opacity: 0.85 }} className="print-assignment-time">
                              {formatTime(assignment.startTime)} - {formatTime(assignment.endTime)}
                            </span>
                          </div>
                          <button
                            style={removeButtonStyle}
                            onClick={(e) => { e.stopPropagation(); handleUnassign(assignment.id, dateStr, room.id); }}
                            title="Remove"
                            className="no-print"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Available Teachers Footer - Hidden when printing */}
        <div style={footerStyle} className="no-print">
          <div style={footerLabelStyle}>
            Available
          </div>
          {weekDates.map(date => {
            const dateStr = dateToString(date);
            const availableUsers = getAvailableUnassignedUsers(dateStr);
            
            return (
              <div key={`avail-${dateStr}`} style={footerCellStyle}>
                {availableUsers.length === 0 ? (
                  <span style={noAvailabilityStyle}>No availability</span>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {availableUsers.map(user => {
                      const userAvail = dataStore.getUserAvailabilityForDate(user.id, dateStr);
                      const availText = userAvail?.slots?.length > 0
                        ? userAvail.slots.map(s => `${formatTime(s.start)}-${formatTime(s.end)}`).join(' & ')
                        : `${formatTime(userAvail?.start)}-${formatTime(userAvail?.end)}`;
                      return (
                        <span
                          key={user.id}
                          style={availableTagStyle}
                          draggable
                          onDragStart={(e) => handleDragStart(e, user.id)}
                          onDragEnd={handleDragEnd}
                          title={`${user.name}: ${availText} - Drag to assign`}
                        >
                          {user.name}
                          <span style={{ fontSize: '0.75rem', opacity: 0.85, marginLeft: '6px' }}>
                            {availText}
                          </span>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* On PTO Row */}
        <div style={ptoFooterStyle} className="print-pto-row">
          <div style={footerLabelStyle} className="print-room-cell">
            On PTO
          </div>
          {weekDates.map(date => {
            const dateStr = dateToString(date);
            const usersOnPTO = getUsersOnPTO(dateStr);
            
            return (
              <div key={`pto-${dateStr}`} style={footerCellStyle} className="print-day-cell">
                {usersOnPTO.length === 0 ? (
                  <span style={noAvailabilityStyle}>—</span>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {usersOnPTO.map(user => {
                      const isPartial = user.ptoDetails?.isPartialDay;
                      const ptoStart = user.ptoDetails?.startTime;
                      const ptoHours = user.ptoDetails?.hours;
                      
                      // Format time for display
                      const formatPtoTime = (timeStr) => {
                        if (!timeStr) return '';
                        const [h, m] = timeStr.split(':').map(Number);
                        const amPm = h >= 12 ? 'PM' : 'AM';
                        const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
                        return `${h12}:${String(m).padStart(2, '0')} ${amPm}`;
                      };
                      
                      return (
                        <span
                          key={user.id}
                          style={isPartial ? partialPtoTagStyle : ptoTagStyle}
                          className="print-pto-badge"
                          title={isPartial 
                            ? `Partial PTO: ${formatPtoTime(ptoStart)} (${ptoHours}h)` 
                            : 'Full day PTO'}
                        >
                          {/* Triangle indicator for partial day */}
                          {isPartial && (
                            <span style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: 0,
                              height: 0,
                              borderStyle: 'solid',
                              borderWidth: '12px 12px 0 0',
                              borderColor: `${colors.red} transparent transparent transparent`
                            }} />
                          )}
                          {user.name}
                          {isPartial && (
                            <span style={{ 
                              marginLeft: '6px', 
                              fontSize: '0.75rem',
                              opacity: 0.9 
                            }}>
                              {formatPtoTime(ptoStart)} ({ptoHours}h)
                            </span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Assignment Modal - Hidden when printing */}
      {selectedCell && (
        <div style={modalOverlayStyle} onClick={() => setSelectedCell(null)} className="no-print">
          <div style={{ ...modalStyle, maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div style={modalTitleStyle}>
              Assign Teacher
            </div>
            <div style={modalSubtitleStyle}>
              {rooms.find(r => r.id === selectedCell.roomId)?.name} — {' '}
              {new Date(selectedCell.date + 'T12:00:00').toLocaleDateString('en-US', { 
                weekday: 'long', 
                month: 'short', 
                day: 'numeric' 
              })}
            </div>

            {/* Available Teachers */}
            <div style={{ fontSize: '0.85rem', color: theme.textSecondary, marginBottom: '8px' }}>
              Select a teacher and time slot:
            </div>

            {getAllAvailableUsers(selectedCell.date).length === 0 ? (
              <p style={{ color: theme.textSecondary, textAlign: 'center', padding: '20px 0' }}>
                No teachers available for this day.
              </p>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {getAllAvailableUsers(selectedCell.date).map(user => {
                  const userAvail = dataStore.getUserAvailabilityForDate(user.id, selectedCell.date);
                  const existingAssignments = getUserAssignmentsOnDate(user.id, selectedCell.date);
                  const availableSlots = getAvailableTimeSlots(user.id, selectedCell.date);
                  const userTimes = modalUserTimes[user.id] || { start: userAvail?.start || '08:00', end: userAvail?.end || '17:00' };
                  const hasOverlap = hasTimeOverlap(user.id, selectedCell.date, userTimes.start, userTimes.end);
                  const noSlotsAvailable = availableSlots.length === 0 && existingAssignments.length > 0;
                  
                  // Check for partial day PTO
                  const ptoDetails = dataStore.getPTODetailsForDate(user.id, selectedCell.date);
                  const hasPartialPTO = ptoDetails?.isPartialDay;
                  
                  return (
                    <div
                      key={user.id}
                      style={{
                        padding: '12px',
                        marginBottom: '10px',
                        border: `1px solid ${hasOverlap ? colors.red : hasPartialPTO ? '#f59e0b' : theme.border}`,
                        borderRadius: '8px',
                        backgroundColor: noSlotsAvailable ? (darkMode ? 'rgba(239,83,80,0.1)' : 'rgba(239,83,80,0.05)') : 
                                         hasPartialPTO ? (darkMode ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.05)') : theme.background
                      }}
                    >
                      {/* Teacher Name */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ color: theme.text, fontWeight: '600', fontSize: '1rem' }}>{user.name}</span>
                      </div>
                      
                      {/* Availability Info - Show all slots */}
                      <div style={{ fontSize: '0.8rem', color: theme.textSecondary, marginBottom: '6px' }}>
                        📅 Availability: {userAvail?.slots?.length > 0 
                          ? userAvail.slots.map(s => `${formatTime(s.start)}-${formatTime(s.end)}`).join(' & ')
                          : `${formatTime(userAvail?.start)} - ${formatTime(userAvail?.end)}`}
                      </div>
                      
                      {/* Partial PTO Warning */}
                      {hasPartialPTO && (
                        <div style={{ fontSize: '0.8rem', color: '#f59e0b', marginBottom: '6px', fontWeight: '600' }}>
                          ⏰ Partial PTO: {formatTime(ptoDetails.startTime)} - {formatTime(ptoDetails.endTime)} ({ptoDetails.hours}h)
                        </div>
                      )}
                      
                      {/* Existing Assignments */}
                      {existingAssignments.length > 0 && (
                        <div style={{ fontSize: '0.8rem', color: colors.blue, marginBottom: '6px' }}>
                          📌 Already assigned: {existingAssignments.map(a => 
                            `${a.roomName} (${formatTime(a.startTime)}-${formatTime(a.endTime)})`
                          ).join(', ')}
                        </div>
                      )}
                      
                      {/* Available Slots */}
                      {existingAssignments.length > 0 && availableSlots.length > 0 && (
                        <div style={{ fontSize: '0.8rem', color: colors.green, marginBottom: '8px' }}>
                          ✅ Open slots: {availableSlots.map(s => `${formatTime(s.start)}-${formatTime(s.end)}`).join(', ')}
                        </div>
                      )}
                      
                      {/* No Slots Warning */}
                      {noSlotsAvailable && (
                        <div style={{ fontSize: '0.8rem', color: colors.red, marginBottom: '8px' }}>
                          ⚠️ No open slots remaining
                        </div>
                      )}
                      
                      {/* Time Selection + Assign Button */}
                      {!noSlotsAvailable && (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          marginTop: '8px',
                          padding: '8px',
                          backgroundColor: theme.backgroundSecondary,
                          borderRadius: '6px'
                        }}>
                          <input
                            type="time"
                            value={userTimes.start}
                            onChange={(e) => setModalUserTimes(prev => ({
                              ...prev,
                              [user.id]: { ...prev[user.id], start: e.target.value }
                            }))}
                            style={{
                              padding: '6px',
                              border: `1px solid ${theme.border}`,
                              borderRadius: '4px',
                              backgroundColor: theme.background,
                              color: theme.text,
                              fontSize: '0.85rem',
                              colorScheme: darkMode ? 'dark' : 'light'
                            }}
                          />
                          <span style={{ color: theme.textSecondary }}>to</span>
                          <input
                            type="time"
                            value={userTimes.end}
                            onChange={(e) => setModalUserTimes(prev => ({
                              ...prev,
                              [user.id]: { ...prev[user.id], end: e.target.value }
                            }))}
                            style={{
                              padding: '6px',
                              border: `1px solid ${theme.border}`,
                              borderRadius: '4px',
                              backgroundColor: theme.background,
                              color: theme.text,
                              fontSize: '0.85rem',
                              colorScheme: darkMode ? 'dark' : 'light'
                            }}
                          />
                          <button
                            style={{
                              marginLeft: 'auto',
                              padding: '6px 14px',
                              backgroundColor: hasOverlap ? colors.orange : colors.green,
                              color: darkMode ? '#fff' : '#333',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: '600'
                            }}
                            onClick={() => handleAssign(user.id, selectedCell.date, selectedCell.roomId, userTimes.start, userTimes.end)}
                            title={hasOverlap ? 'Warning: Times overlap with existing assignment' : 'Assign teacher'}
                          >
                            {hasOverlap ? '⚠️ Assign' : 'Assign'}
                          </button>
                        </div>
                      )}
                      
                      {/* Overlap Warning */}
                      {hasOverlap && !noSlotsAvailable && (
                        <div style={{ fontSize: '0.75rem', color: colors.red, marginTop: '6px' }}>
                          ⚠️ Selected times overlap with an existing assignment
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <button 
              style={modalCancelBtnStyle}
              onClick={() => setSelectedCell(null)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = darkMode ? theme.border : '#d1d5db';
                e.currentTarget.style.borderColor = colors.blue;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = darkMode ? theme.backgroundSecondary : '#e5e7eb';
                e.currentTarget.style.borderColor = theme.border;
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Edit Assignment Modal */}
      {editingAssignment && (
        <div style={modalOverlayStyle} onClick={() => setEditingAssignment(null)} className="no-print">
          <div style={{ ...modalStyle, maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div style={modalTitleStyle}>
              Edit Assignment Time
            </div>
            <div style={modalSubtitleStyle}>
              {editingAssignment.userName} — {editingAssignment.roomName}
              <br />
              {new Date(editingAssignment.dateStr + 'T12:00:00').toLocaleDateString('en-US', { 
                weekday: 'long', 
                month: 'short', 
                day: 'numeric' 
              })}
            </div>

            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              marginTop: '20px',
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: theme.backgroundSecondary,
              borderRadius: '8px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '0.8rem', color: theme.textSecondary }}>Start Time</label>
                <input
                  type="time"
                  value={editingAssignment.startTime}
                  onChange={(e) => setEditingAssignment(prev => ({ ...prev, startTime: e.target.value }))}
                  style={{
                    padding: '10px',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    backgroundColor: theme.background,
                    color: theme.text,
                    fontSize: '1rem',
                    colorScheme: darkMode ? 'dark' : 'light'
                  }}
                />
              </div>
              <span style={{ color: theme.textSecondary, marginTop: '20px' }}>to</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '0.8rem', color: theme.textSecondary }}>End Time</label>
                <input
                  type="time"
                  value={editingAssignment.endTime}
                  onChange={(e) => setEditingAssignment(prev => ({ ...prev, endTime: e.target.value }))}
                  style={{
                    padding: '10px',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    backgroundColor: theme.background,
                    color: theme.text,
                    fontSize: '1rem',
                    colorScheme: darkMode ? 'dark' : 'light'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                style={modalCancelBtnStyle}
                onClick={() => setEditingAssignment(null)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = darkMode ? theme.border : '#d1d5db';
                  e.currentTarget.style.borderColor = colors.blue;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = darkMode ? theme.backgroundSecondary : '#e5e7eb';
                  e.currentTarget.style.borderColor = theme.border;
                }}
              >
                Cancel
              </button>
              <button 
                style={{
                  ...modalCancelBtnStyle,
                  backgroundColor: colors.green,
                  color: darkMode ? '#fff' : '#333',
                  border: 'none'
                }}
                onClick={handleSaveEditedAssignment}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Toast */}
      {message && <div style={messageStyle} className="no-print">{message}</div>}

      {/* Print Modal */}
      <RoomCardPrint
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        currentMonday={currentMonday}
      />

      {/* Create Template Modal */}
      {showCreateTemplateModal && (
        <div style={modalOverlayStyle} onClick={() => setShowCreateTemplateModal(false)}>
          <div style={{ ...modalStyle, maxWidth: '450px' }} onClick={(e) => e.stopPropagation()}>
            <div style={modalTitleStyle}>
              📋 Save Week as Template
            </div>
            
            <p style={{ color: theme.textSecondary, marginBottom: '16px', fontSize: '0.9rem' }}>
              Create a reusable template from this week's schedule. You can apply it to future weeks from the Templates page.
            </p>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '6px', color: theme.text }}>
                Template Name *
              </label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Standard Week, Summer Schedule"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.backgroundSecondary,
                  color: theme.text,
                  fontSize: '0.95rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '6px', color: theme.text }}>
                Description (optional)
              </label>
              <textarea
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Brief notes about this schedule..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.backgroundSecondary,
                  color: theme.text,
                  fontSize: '0.95rem',
                  boxSizing: 'border-box',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.backgroundSecondary,
                  color: theme.text,
                  cursor: 'pointer',
                  fontSize: '0.95rem'
                }}
                onClick={() => {
                  setShowCreateTemplateModal(false);
                  setTemplateName('');
                  setTemplateDescription('');
                }}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: colors.green,
                  color: darkMode ? '#fff' : '#333',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '500'
                }}
                onClick={handleCreateTemplate}
              >
                Create Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Template Modal */}
      {showApplyTemplateModal && (
        <div style={modalOverlayStyle} onClick={() => setShowApplyTemplateModal(false)}>
          <div style={{ ...modalStyle, maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div style={modalTitleStyle}>
              📥 Apply Template to This Week
            </div>
            
            {templates.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: theme.textSecondary }}>
                <div style={{ fontSize: '2rem', marginBottom: '10px' }}>📋</div>
                <p>No templates saved yet.</p>
                <p style={{ fontSize: '0.9rem' }}>Create a template first using "Save as Template".</p>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontWeight: '500', marginBottom: '6px', color: theme.text }}>
                    Select Template
                  </label>
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: `1px solid ${theme.border}`,
                      backgroundColor: theme.backgroundSecondary,
                      color: theme.text,
                      fontSize: '0.95rem',
                      boxSizing: 'border-box'
                    }}
                  >
                    {templates.map(t => {
                      const stats = dataStore.getTemplateStats(t.id);
                      return (
                        <option key={t.id} value={t.id}>
                          {t.name} ({stats?.totalAssignments || 0} assignments)
                        </option>
                      );
                    })}
                  </select>
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontWeight: '500', marginBottom: '10px', color: theme.text }}>
                    Options
                  </label>
                  
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer', color: theme.text }}>
                    <input
                      type="checkbox"
                      checked={applyOptions.skipPTO}
                      onChange={(e) => setApplyOptions({ ...applyOptions, skipPTO: e.target.checked })}
                    />
                    Skip teachers on PTO
                  </label>
                  
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer', color: theme.text }}>
                    <input
                      type="checkbox"
                      checked={applyOptions.respectAvailability}
                      onChange={(e) => setApplyOptions({ ...applyOptions, respectAvailability: e.target.checked })}
                    />
                    Respect availability settings
                  </label>
                  
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: theme.text }}>
                    <input
                      type="checkbox"
                      checked={applyOptions.overwriteExisting}
                      onChange={(e) => setApplyOptions({ ...applyOptions, overwriteExisting: e.target.checked })}
                    />
                    Overwrite existing assignments
                  </label>
                </div>
              </>
            )}
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.backgroundSecondary,
                  color: theme.text,
                  cursor: 'pointer',
                  fontSize: '0.95rem'
                }}
                onClick={() => setShowApplyTemplateModal(false)}
              >
                Cancel
              </button>
              {templates.length > 0 && (
                <button
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: colors.purple || '#9333ea',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: '500'
                  }}
                  onClick={handleApplyTemplate}
                >
                  Apply Template
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Announcement Modal */}
      {showAnnouncementModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: theme.cardBackground,
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '450px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: theme.text, fontSize: '1.2rem' }}>
              {editingAnnouncement ? 'Edit Announcement' : 'Add Announcement'}
            </h3>
            
            {/* Icon Picker */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', color: theme.textSecondary, fontSize: '0.85rem' }}>
                Icon
              </label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {['ℹ️', '⏰', '🚗', '⚠️', '📋', '🎉', '📣', '💡', '🔔', '✨'].map(icon => (
                  <button
                    key={icon}
                    onClick={() => setAnnouncementForm({ ...announcementForm, icon })}
                    style={{
                      padding: '8px 12px',
                      fontSize: '1.2rem',
                      borderRadius: '6px',
                      border: announcementForm.icon === icon 
                        ? `2px solid ${colors.blue}` 
                        : `1px solid ${theme.border}`,
                      backgroundColor: announcementForm.icon === icon 
                        ? (darkMode ? 'rgba(119,184,219,0.2)' : 'rgba(119,184,219,0.1)')
                        : theme.backgroundSecondary,
                      cursor: 'pointer'
                    }}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Date Picker */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', color: theme.textSecondary, fontSize: '0.85rem' }}>
                Date
              </label>
              <input
                type="date"
                value={announcementForm.date}
                onChange={(e) => setAnnouncementForm({ ...announcementForm, date: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.backgroundSecondary,
                  color: theme.text,
                  fontSize: '0.95rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Message */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', color: theme.textSecondary, fontSize: '0.85rem' }}>
                Message
              </label>
              <textarea
                value={announcementForm.message}
                onChange={(e) => setAnnouncementForm({ ...announcementForm, message: e.target.value })}
                placeholder="e.g., Sarah arriving at 9:30am"
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.backgroundSecondary,
                  color: theme.text,
                  fontSize: '0.95rem',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
                autoFocus
              />
            </div>
            
            {/* Buttons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAnnouncementModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.backgroundSecondary,
                  color: theme.text,
                  cursor: 'pointer',
                  fontSize: '0.95rem'
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveAnnouncement}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: colors.blue,
                  color: darkMode ? '#fff' : '#333',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '500'
                }}
              >
                {editingAnnouncement ? 'Save Changes' : 'Add Announcement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Footer - Only shows when printing */}
      <div className="print-footer">
        Printed on {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
      </div>
    </div>
  );
}

export default ScheduleManagement;
